<template>
  <transition name="overlay-fade">
    <div class="alternate-modal-overlay" @click="close" v-if="visible">
      <transition name="modal-pop">
        <div
          class="alternate-modal"
          :class="[modalSizeClass]"
          :data-mode="innerKey"
          @click.stop
          role="dialog"
          aria-modal="true"
          aria-label="Alternate Materials Modal"
          :key="visibleKey"
        >
          <!-- Header -->
          <header class="am-header">
            <div class="am-header__titles">
              <h3 class="am-title">{{ currentTitle }}</h3>
              <p class="am-subtitle">{{ currentSubtitle }}</p>
            </div>

            <button class="am-close-btn" type="button" @click="close" aria-label="Close modal">✕</button>
          </header>

          <!-- Body: internal pages animate using keyed transition -->
          <section class="am-body">
            <transition :name="pageTransitionName" mode="out-in">
              <div :key="innerKey">
                <!-- PREVIEW MODE -->
                <div v-if="innerKey === 'preview'" class="preview-mode">
                  <div class="preview-banner">
                    <h4 class="preview-banner__title">Proposed Workflow Plan</h4>
                    <p class="preview-banner__desc">The following workflow steps will be initiated upon submission.</p>
                  </div>

                  <div class="workflow-section">
                    <div class="workflow-section__header">
                      <img class="alternate-icon" src="~@/assets/images/icons/alternate-link.svg" alt="">
                      <h4 class="workflow-title">Workflow Preview</h4>
                      <span class="workflow-count">{{ workflowSteps.length }} Steps</span>
                    </div>

                    <div class="workflow-list" ref="workflowList">
                      <div
                        class="workflow-step"
                        v-for="(step, index) in workflowSteps"
                        :key="step.id || index"
                      >
                        <div class="step-index">{{ index + 1 }}</div>

                        <div class="step-card">
                          <div class="step-card__pointer" aria-hidden="true"></div>

                          <div class="step-card__content">
                            <div class="step-card__left">
                              <h5 class="step-name">{{ step.name || 'Untitled step' }}</h5>
                              <p class="step-stage" v-if="step.stage">{{ step.stage }}</p>
                            </div>
                            <div class="step-duration">⏱ {{ formatDuration(step) }}</div>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div class="preview-actions">
                      <button type="button" class="alt-btn btn-ghost" @click="goTo('add')">Back</button>
                      <button type="button" class="alt-btn" @click="handleConfirm" :disabled="submitting">
                        {{ submitting ? 'Submitting...' : 'Submit for Approval' }}
                      </button>
                    </div>
                  </div>
                </div>

                <!-- MANAGE MODE -->
                <div v-else-if="innerKey === 'manage'" class="manage-mode">
                  <div class="current-alternates">
                    <h4 class="section-title">CURRENT ALTERNATES</h4>

                    <div v-if="alternateList.length > 0" class="alternates-list">
                      <div
                        v-for="alt in alternateList"
                        :key="alt.id || alt.materialId"
                        class="alternate-row"
                        @click="handleViewAlternate(alt)"
                        role="button"
                        tabindex="0"
                        @keydown.enter="handleViewAlternate(alt)"
                      >
                        <div class="alternate-row__left">
                          <div class="alternate-name">{{ alt.name || '--' }}</div>
                          <div class="alternate-id">{{ alt.materialId || '--' }}</div>
                        </div>

                        <div class="alternate-row__right">
                          <span
                            class="alternate-status"
                            :data-status="alt.status || 'Proposed'"
                          >
                            {{ alt.status || 'Proposed' }}
                          </span>
                        </div>
                      </div>
                    </div>

                    <p v-else class="no-alternates">No alternate materials defined.</p>

                    <div class="add-cta">
                      <button type="button" class="add-cta__btn" @click="goTo('add')"><span>+ </span> Add Alternate</button>
                    </div>
                  </div>
                </div>
                <div v-else-if="innerKey === 'view'" class="view-mode">
                  <workflow-instance-edit-panel
                    :panel-style="'alternate'"
                    v-if="viewingAlternate?.instance_id"
                    ref="WorkflowDetailsPanel"
                    :wfInstanceId="viewingAlternate?.instance_id"
                    :feature="config.CORE_FEATURES.MATERIAL"
                    @goBack="goBack"
                    @material-workflow-cancelled="cancelWorkflow"
                    @finishWorkflow="finishWorkflow"
                  />
                </div>

                <!-- ADD MODE -->
                <div v-else-if="innerKey === 'add'" class="add-form">
                  <form @submit.prevent="handleSubmit" novalidate>
                    <h4 style="color: #E59A00;" class="section-title alt">PROPOSE NEW ALTERNATE</h4>

                    <div class="form-grid">
                      <div class="form-main">
                        <label style="margin-bottom: -10px;" class="form-label">
                          Select from Inventory<span class="req">*</span>
                        </label>
                        <!-- Custom searchable dropdown -->
                        <div class="inventory-dropdown" @click.stop>
                          <div
                            class="inventory-dropdown__control"
                            @click.stop="toggleDropdown"
                            :class="{ open: dropdownOpen }"
                            role="button"
                            tabindex="0"
                            @keydown.enter.prevent="toggleDropdown"
                            ref="dropdownControl"
                          >
                            <div class="selected">
                              <span v-if="newAltName" class="selected-name" :title="newAltName">{{ newAltName }}</span>
                              <span v-if="newAltName && selectedShowId" class="selected-id" :title="newAltId">({{ newAltId }})</span>
                              <span v-else-if="!newAltName" class="muted">-- Select Material --</span>
                            </div>
                            <div class="caret">▾</div>
                          </div>

                          <div v-if="dropdownOpen" class="inventory-dropdown__menu" ref="dropdownMenu" @click.stop>
                            <div class="inventory-dropdown__search">
                              <input
                                v-model="materialsQueryLocal"
                                class="inventory-search"
                                placeholder="Search materials..."
                                aria-label="Search materials"
                                ref="searchInput"
                                @input="onMaterialsSearchInput"
                              />
                            </div>

                            <div
                              class="inventory-dropdown__list"
                              role="listbox"
                              ref="dropdownList"
                              @scroll.passive="onDropdownScroll"
                            >
                              <div v-if="materialsLoading && materials.length === 0" class="dropdown-loading">
                                <loading-circle />
                              </div>

                              <div v-if="!materialsLoading && displayedMaterials.length === 0" class="no-results">No materials found.</div>

                              <div
                                v-for="mat in displayedMaterials"
                                :key="mat.key || mat.id || mat.custom_material_id"
                                class="inventory-option"
                                role="option"
                                @click="selectInventoryItem(mat)"
                                @keydown.enter.prevent="selectInventoryItem(mat)"
                                tabindex="0"
                              >
                                <div class="opt-left single-line">
                                  <span class="opt-name-compact" :title="mat.material_name">{{ mat.material_name || mat.name || 'Unnamed' }}</span>
                                  <span v-if="mat.showId && (mat.custom_material_id || mat.id)" class="opt-id-compact" :title="mat.custom_material_id || mat.id">({{ mat.custom_material_id || mat.id }})</span>
                                </div>
                              </div>

                              <!-- inline loading-more spinner -->
                              <div v-if="materialsLoadingMore" class="dropdown-loading-more">
                                <loading-circle />
                              </div>
                            </div>
                          </div>
                        </div>
                        <!-- end dropdown -->

                        <div class="two-col">
                          <div class="field">
                            <label class="form-label">Alternate Name</label>
                            <input
                              class="form-input"
                              :class="{ 'muted-input': !selectedInventoryId && !newAltName }"
                              v-model="newAltName"
                              :readonly="!!selectedInventoryId"
                              required
                              placeholder="Name"
                              aria-required="true"
                            />
                          </div>

                          <div class="field">
                            <label class="form-label">Material ID</label>
                            <input
                              class="form-input"
                              :class="{ 'muted-input': !selectedInventoryId && !newAltId }"
                              v-model="newAltId"
                              :readonly="!!selectedInventoryId"
                              required
                              placeholder="ID / Code"
                              aria-required="true"
                            />
                          </div>
                        </div>

                        <div class="field">
                          <label class="form-label">
                            Reason for Proposal <span class="req">*</span>
                          </label>
                          <textarea
                            class="form-textarea"
                            v-model="reason"
                            rows="4"
                            placeholder="e.g. Cost saving, Supply chain risk, Better availability"
                            aria-required="true"
                          ></textarea>
                        </div>

                        <div class="form-actions">
                          <button type="button" class="btn btn-cancel" @click="goTo('manage')">Cancel</button>
                          <button type="submit" class="alt-btn" :disabled="!canInitiate">Initiate Workflow</button>
                        </div>
                      </div>

                      <aside class="comparison-preview" v-if="showComparisonPreview">
                        <h5 class="preview-title">Comparison Preview</h5>

                        <div class="preview-block primary">
                          <div class="preview-label">Primary Material</div>
                          <div class="preview-name">{{ item.name || '--' }}</div>
                          <div class="preview-id">{{ item.materialId || '--' }}</div>
                        </div>

                        <div class="preview-arrow" aria-hidden="true">
                          <img src="~@/assets/images/icons/arrow-down.svg" alt="" />
                        </div>

                        <div class="preview-block alternate">
                          <div class="preview-label" style="color: #2563EB;">Proposed Alternate</div>
                          <div class="preview-name">
                            <span v-if="newAltName">{{ newAltName }}</span>
                            <span v-else class="muted">Select Material...</span>
                          </div>
                          <div class="preview-id">{{ newAltId || '--' }}</div>

                          <div v-if="reason" class="preview-reason">
                            <div class="preview-reason__label">Reason</div>
                            <div class="preview-reason__text">"{{ reason }}"</div>
                          </div>
                        </div>
                      </aside>
                    </div>
                  </form>
                </div>
              </div>
            </transition>
          </section>
        </div>
      </transition>
    </div>
  </transition>
</template>

<script>
import Loader from '@/plugins/loader'
import config from '@/config'
import { warning } from '@/plugins/notification'
import workflowInstanceEditPanel from '@/views/FORM/workflowInstanceEditPanel.vue'
import { getAlternateMaterialWorkflow, AlternateMaterials, AddAlternateMaterial, GetMaterialMasterData } from '@/api'
import loadingCircle from '../common/loadingCircle.vue'
import { mapGetters } from 'vuex'

export default {
  name: 'AlternateMaterialsModal',
  components: { loadingCircle, workflowInstanceEditPanel },
  props: {
    item: {
      type: Object,
      default: () => ({ name: '', materialId: '', alternates: [] })
    },
    allItems: {
      type: Array,
      default: () => []
    },
    size: {
      type: String,
      default: 'md',
      validator: (val) => ['sm', 'md', 'lg'].includes(val)
    },
    visible: {
      type: Boolean,
      default: true
    }
  },

  data () {
    return {
      config: config,
      apiAlternates: [],
      apiWorkflowSteps: [],
      apiLoaded: false,
      isAdding: false,
      showPreview: false,
      viewingAlternate: null, // currently selected existing alternate for workflow view
      selectedInventoryId: '',
      newAltName: '',
      newAltId: '',
      reason: '',
      dropdownOpen: false,
      searchQuery: '',
      submitting: false,

      // materials lazy-loading state
      materials: [],
      materialsJump: 0, // offset (jump)
      materialsPerPage: 40,
      materialsHasMore: true,
      materialsLoading: false,
      materialsLoadingMore: false,
      materialsQueryLocal: '',

      // debounce timer
      materialsSearchTimer: null,
      selectedNameMaxChars: 36 // if name length <= this, we show ID next to name
    }
  },

  computed: {
    ...mapGetters(['user']),
    currentUserId () {
      return this.user?.userId || null
    },
    alternateList () {
      if (this.apiAlternates && this.apiAlternates.length > 0) {
        return this.apiAlternates.map(a => {
          const alt = a.alternate_material || {}
          const workflowInst = a.workflow_instance || {}
          const stateName = workflowInst && workflowInst.state_value && workflowInst.state_value.name
            ? workflowInst.state_value.name
            : null

          return {
            id: alt.id,
            materialId: alt.custom_material_id || alt.materialId || alt.id,
            name: alt.material_name || alt.name || '',
            status: stateName || a.status || 'Proposed',
            instance_id: a?.workflow_instance?.id || null
          }
        })
      }
      return this.item && this.item.alternates ? this.item.alternates : []
    },

    excludedAlternateIdsSet () {
      const set = new Set()
      ;(this.apiAlternates || []).forEach(a => {
        const id = a && a.alternate_material && a.alternate_material.custom_material_id
        if (id) set.add(String(id))
      })
      return set
    },
    primaryMaterialKeys () {
      const keys = new Set()
      if (this.item) {
        if (this.item.id != null) keys.add(String(this.item.id))
        if (this.item.materialId != null) keys.add(String(this.item.materialId))
      }
      return keys
    },
    workflowSteps () {
      return this.apiWorkflowSteps || []
    },

    modalSizeClass () {
      return `size-${this.size}`
    },

    showComparisonPreview () {
      return true
    },

    innerKey () {
      if (this.showPreview) return 'preview'
      if (this.isAdding) return 'add'
      if (this.viewingAlternate) return 'view'
      return 'manage'
    },

    visibleKey () {
      const itemId = this.item?.id || this.item?.materialId || 'new'
      return `${itemId}-${this.modalSizeClass}`
    },

    pageTransitionName () {
      return 'page-slide'
    },

    currentTitle () {
      if (this.showPreview) return 'Confirm Workflow'
      return 'Manage Alternate Materials'
    },

    currentSubtitle () {
      if (this.showPreview) return 'Review the approval process'
      const name = this.item?.name || '--'
      const id = this.item?.materialId || '--'
      return `For: ${name} (${id})`
    },

    canInitiate () {
      return Boolean(
        this.newAltName && this.newAltName.trim() &&
        this.newAltId && this.newAltId.trim() &&
        this.reason && this.reason.trim()
      )
    },
    displayedMaterials () {
      const q = (this.materialsQueryLocal || '').trim().toLowerCase()
      const results = (this.materials || [])
        .filter(m => {
          if (m.status === 2) return false
          const keyId = String(m.custom_material_id || m.id || '').trim()
          if (!keyId) return false
          if (this.excludedAlternateIdsSet.has(keyId)) return false
          if (this.primaryMaterialKeys.has(String(m.id)) || this.primaryMaterialKeys.has(String(m.custom_material_id))) return false

          if (!q) return true

          const name = String(m.material_name || m.name || '').toLowerCase()
          const id = String(m.custom_material_id || m.id || '').toLowerCase()
          return name.includes(q) || id.includes(q)
        })
        .map(m => {
          const name = String(m.material_name || m.name || '')
          const id = String(m.custom_material_id || m.id || '')
          const showId = name.trim().length > 0 && name.trim().length <= this.selectedNameMaxChars
          return {
            key: `${m.id || ''}-${m.custom_material_id || ''}`,
            id: m.id,
            custom_material_id: m.custom_material_id,
            material_name: name,
            showId,
            rawId: id
          }
        })
      return results
    },
    selectedShowId () {
      if (!this.newAltName) return false
      const nameLen = String(this.newAltName || '').trim().length
      return nameLen > 0 && nameLen <= this.selectedNameMaxChars && Boolean(this.newAltId)
    }
  },

  watch: {
    item: {
      handler () {
        this.resetForm()
        this.fetchData()
      },
      deep: true
    },

    dropdownOpen (isOpen) {
      if (!isOpen) {
        this.materialsQueryLocal = ''
      } else {
        this.materialsQueryLocal = ''
        this.fetchMaterials({ q: '', reset: true, wildcard: false })
        this.$nextTick(() => {
          if (this.$refs.searchInput) {
            this.$refs.searchInput.focus()
          }
        })
      }
    }
  },

  created () {
    this.fetchData()
  },

  mounted () {
    document.addEventListener('pointerdown', this.onDocumentClick, true)
  },

  beforeUnmount () {
    document.removeEventListener('pointerdown', this.onDocumentClick, true)
  },

  methods: {
    cancelWorkflow () {
      this.viewingAlternate = null
      this.isAdding = false
      this.showPreview = false
      this.fetchData() // refresh alternates list (important)
    },
    finishWorkflow () {
      this.$refs.WorkflowDetailsPanel.updateWorkflowMaterial(true)
    },
    onDocumentClick (e) {
      if (!this.dropdownOpen) return

      const menu = this.$refs.dropdownMenu
      const control = this.$refs.dropdownControl
      const clickTarget = e.target

      if (menu && menu.contains(clickTarget)) return
      if (control && control.contains(clickTarget)) return

      this.dropdownOpen = false
    },

    async fetchData () {
      const loader = new Loader()
      loader.show()

      try {
        const [altsRes, workflowRes] = await Promise.all([
          AlternateMaterials(this.item.id),
          getAlternateMaterialWorkflow()
        ])

        this.apiAlternates = altsRes?.alternate_materials || []
        this.apiWorkflowSteps = (workflowRes?.workflow_stages || []).map(s => ({
          id: s.id,
          name: s.name,
          duration: s.duration,
          duration_type: s.duration_type ?? s.durationType ?? null,
          stage: s.stage ?? ''
        }))
      } catch (err) {
        console.error('API error:', err)
        this.$emit('fetch-error', err)
      } finally {
        loader.hide()
        this.apiLoaded = true
      }
    },

    resetForm () {
      this.isAdding = false
      this.showPreview = false
      this.viewingAlternate = null
      this.selectedInventoryId = ''
      this.newAltName = ''
      this.newAltId = ''
      this.reason = ''
      this.dropdownOpen = false
      this.searchQuery = ''
    },

    close () {
      this.$emit('close')
      this.resetForm()
    },

    toggleDropdown () {
      this.dropdownOpen = !this.dropdownOpen
    },
    async fetchMaterials ({ q = '', reset = false, wildcard = false } = {}) {
      if (reset) {
        this.materials = []
        this.materialsJump = 0
        this.materialsHasMore = true
      }

      if (!this.materialsHasMore && !reset) return

      const isInitial = reset || this.materials.length === 0
      if (isInitial) {
        this.materialsLoading = true
      } else {
        this.materialsLoadingMore = true
      }
      const trimmed = (q || '').trim()
      const serverQuery = trimmed ? (wildcard ? `%${trimmed}%` : trimmed) : undefined

      const filter = {
        type: 'material',
        jump: this.materialsJump,
        perPage: this.materialsPerPage,
        searchKeyword: serverQuery
      }

      try {
        const res = await GetMaterialMasterData(filter, false, undefined)
        const page = (res?.core_material_master || []).map(m => ({
          id: m.id,
          custom_material_id: m.custom_material_id,
          material_name: m.material_name,
          status: m.status,
          category: m.material_group || (m.resource_group_details?.[0]?.name) || m.material_type || ''
        }))

        const excluded = this.excludedAlternateIdsSet
        const filteredPage = page.filter(p => {
          const key = String(p.custom_material_id || p.id || '')
          if (!key) return false
          if (excluded.has(key)) return false
          if (this.primaryMaterialKeys.has(String(p.id)) || this.primaryMaterialKeys.has(String(p.custom_material_id))) return false
          return true
        })

        if (reset) {
          this.materials = filteredPage
        } else {
          this.materials = this.materials.concat(filteredPage)
        }

        const returnedCount = (res?.core_material_master || []).length
        this.materialsJump += returnedCount
        this.materialsHasMore = returnedCount === this.materialsPerPage
      } catch (err) {
        console.error('fetchMaterials error', err)
        this.$emit('materials-fetch-error', err)
      } finally {
        this.materialsLoading = false
        this.materialsLoadingMore = false
      }
    },

    onMaterialsSearchInput () {
      if (this.materialsSearchTimer) clearTimeout(this.materialsSearchTimer)
      this.materialsSearchTimer = setTimeout(() => {
        const q = (this.materialsQueryLocal || '').trim()
        this.materialsJump = 0
        this.materialsHasMore = true
        this.fetchMaterials({ q, reset: true, wildcard: !!q })
      }, 200)
    },

    onDropdownScroll (e) {
      const el = e.target
      if (!el) return
      const nearBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - 80
      if (nearBottom && this.materialsHasMore && !this.materialsLoadingMore && !this.materialsLoading) {
        const q = (this.materialsQueryLocal || '').trim()
        this.fetchMaterials({ q, reset: false, wildcard: !!q })
      }
    },

    loadMoreMaterials () {
      if (this.materialsHasMore && !this.materialsLoadingMore) {
        const q = (this.materialsQueryLocal || '').trim()
        this.fetchMaterials({ q, reset: false, wildcard: !!q })
      }
    },

    /* -----------------------------
       Selection
       ----------------------------- */
    selectInventoryItem (mat) {
      this.selectedInventoryId = mat.id || mat.custom_material_id || mat.materialId || ''
      this.newAltName = mat.material_name || mat.name || ''
      this.newAltId = mat.custom_material_id || mat.id || mat.materialId || ''
      this.dropdownOpen = false
    },

    handleSubmit () {
      if (!this.canInitiate) {
        this.$emit('validation-error', { message: 'Please select an alternate (name + ID) and provide a reason.' })
        return
      }
      this.showPreview = true
      this.isAdding = false
      this.viewingAlternate = null
    },

    async handleConfirm () {
      const primaryId = this.item?.id || this.item?.materialId

      if (!primaryId) {
        this.$emit('validation-error', { message: 'Primary material ID missing.' })
        return
      }

      if (!this.selectedInventoryId && !(this.newAltId && this.newAltId.trim())) {
        this.$emit('validation-error', { message: 'Please select an alternate material.' })
        return
      }

      const loader = new Loader()
      loader.show()
      this.submitting = true

      const variables = {
        alternate_material_ids: [this.selectedInventoryId || this.newAltId],
        material_id: primaryId,
        reason: this.reason?.trim() || null,
        start_step_id: this.apiWorkflowSteps?.[0]?.id || null,
        step_assignees: this.currentUserId ? [this.currentUserId] : []
      }

      try {
        const res = await AddAlternateMaterial(variables)
        warning(`Alternate material request sent for ${this.item.name} material. Pending Approval`)
        this.$emit('add-alternate-success', { variables, res })
        this.close()
      } catch (err) {
        console.error('AddAlternateMaterial error:', err)
        this.$emit('add-alternate-failed', err)
      } finally {
        this.submitting = false
        loader.hide()
      }
    },

    handleViewAlternate (alternate) {
      // Switch into workflow view mode for this alternate
      this.viewingAlternate = alternate
      this.isAdding = false
      this.showPreview = false
      // this.$emit('view-alternate', alternate)
    },

    goBack () {
      this.fetchData()
      this.viewingAlternate = null
    },

    goTo (mode) {
      this.isAdding = mode === 'add'
      this.showPreview = mode === 'preview'
      if (mode !== 'view') {
        this.viewingAlternate = null
      }
    },

    formatDuration (step) {
      if (!step) return '-'

      const duration = step.duration ?? step.duration_value
      if (duration == null) return '-'

      if (typeof duration === 'string' && /\D/.test(duration)) {
        return duration
      }

      return `${duration} ${duration === 1 ? 'Day' : 'Days'}`
    }
  }
}
</script>

<style lang="scss" scoped>
/* overlay */
.alternate-modal-overlay {
  position: fixed;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem;
  z-index: 1100;
  background-color: rgba(0, 0, 0, 0.48);
  -webkit-backdrop-filter: blur(4px);
  backdrop-filter: blur(4px);
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, "Inter", sans-serif;
}
/* modal core */
.alternate-modal {
  width: 100%;
  max-width: 760px;
  background: #ffffff;
  border-radius: 12px;
  box-shadow: 0 22px 44px rgba(2,6,23,0.18);
  overflow: hidden;
  display: flex;
  flex-direction: column;
  transition:
    transform 220ms cubic-bezier(.2,.9,.2,1),
    max-height 220ms cubic-bezier(.2,.9,.2,1),
    opacity 200ms ease;
  max-height: 68vh;
  min-height: 340px;
  color: #111827;
  font-size: var(--text-m);
  line-height: 1.35;
}
/* gentle scale when moving to larger screens (add/preview) */
.alternate-modal[data-mode="add"],
.alternate-modal[data-mode="preview"],
.alternate-modal[data-mode="view"] {
  max-height: 78vh;
  min-height: 420px;
}
/* manage mode default */
.alternate-modal[data-mode="manage"] {
  min-height: 0;
  max-height: 64vh;
  transform: scale(1);
}
/* header */
.am-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 18px;
  background: transparent;
  border-bottom: 1px solid rgba(14,20,24,0.06);
}
.am-title { margin-bottom: 5px; font-size: 1.125rem; font-weight: 700; color: #111827; }
.am-subtitle { margin: 0; font-size: var(--text-s); font-weight: 600; color: #6B7280; }
.am-close-btn { background: transparent; border: none; font-size: var(--text-xl); color: #9CA3AF; cursor: pointer; padding: 6px; }
/* body */
.am-body { padding: 16px 18px; overflow-y: auto; flex: 1; min-height: 0; }
/* preview banner */
.preview-banner { background: #F8FAFF; border: 1px solid #DBEAFE; border-radius: 8px; padding: 25px 10px; margin-bottom: 15px; }
.preview-banner__title { margin: 0; font-size: var(--text-l); font-weight: 700; color: #1E3A8A; margin-bottom: 10px; }
.preview-banner__desc { margin: 0; font-size: var(--text-s); color: #1E40AF; font-weight: 400; }

/* workflow header */
.workflow-section__header { display:flex; align-items:center; gap:8px; margin-bottom:12px; }
.alternate-icon { width: 20px; height: 20px; }
.workflow-icon { width:18px; height:18px; object-fit:contain; display:inline-block; }
.workflow-title { font-weight:700; font-size:14px; color:#111827; margin-right:5px; }
.workflow-count { background:#F3F4F6; padding:4px 8px; border-radius:999px; font-size:12px; color:#6B7280; font-weight:700; }

/* workflow list: visual timeline line and spacing */
.workflow-list {
  --connector-extend: 48px;
  display:flex;
  flex-direction:column;
  gap:18px;
  padding-left: 38px;
  position: relative;
  margin-bottom:12px;
}

/* workflow step */
.workflow-step {
  display:flex;
  gap:10px;
  align-items:flex-start;
  position: relative;
}

/* step index */
.step-index {
  width:28px;
  height:28px;
  min-width:28px;
  border-radius:9999px;
  background:#ffffff;
  border:1.5px solid #111827;
  color:#111827;
  display:flex;
  align-items:center;
  justify-content:center;
  font-weight:700;
  font-size:12px;
  transition: background .12s ease, color .12s ease, border-color .12s ease;
  position: absolute;
  left: -38px;
  z-index: 3;
  box-shadow: 0 1px 0 rgba(2,6,23,0.04);
}

/* connector segment */
.step-index::after {
  content: "";
  position: absolute;
  left: 50%;
  transform: translateX(-50%);
  top: calc(100% + 2px);
  width: 2px;
  height: calc(var(--connector-extend) - 4px);
  background: #EEF2F6;
  border-radius: 2px;
  z-index: 1;
}

/* hide connector for last step */
.workflow-step:last-child .step-index::after { display: none; }

/* step card */
.step-card {
  position:relative;
  flex:1;
  background:#ffffff;
  border:1px solid #EEF2F6;
  border-radius:8px;
  padding:10px;
  box-shadow:none;
  transition: transform .14s ease, box-shadow .14s ease;
  transform: translateX(0) translateY(0);
  z-index: 2;
  will-change: transform, box-shadow;
}

/* pointer */
.step-card__pointer {
  position: absolute;
  width: 12px;
  height: 12px;
  left: -6px;
  top: 16px;
  background: #ffffff;
  border-left: 1px solid #EEF2F6;
  border-top: 1px solid #EEF2F6;
  transform: rotate(45deg) scale(0.9);
  opacity: 0;
  transition: opacity .12s ease, transform .12s ease;
  pointer-events: none;
}

/* card content */
.step-card__content { display:flex; align-items:flex-start; justify-content:space-between; gap:10px; }
.step-name { margin:0; font-size:14px; font-weight:600; color:#111827; }
.step-stage { margin:4px 0 0 0; font-size:11px; color:#6B7280; text-transform:uppercase; letter-spacing:0.04em; }
.step-duration { font-size:12px; color:#6B7280; background:#F9FAFB; padding:6px 8px; border-radius:6px; border:1px solid #F3F4F6; white-space:nowrap; }

/* hover state */
.workflow-step:hover .step-card {
  transform: translateX(10px) translateY(-4px);
  box-shadow: 0 12px 28px rgba(2,6,23,0.12);
  border-color: rgba(14,20,24,0.06);
  z-index: 4;
}
.workflow-step:hover .step-card__pointer {
  opacity: 1;
  transform: rotate(45deg) scale(1);
}

/* index hover */
.workflow-step:hover .step-index {
  background:#111827;
  color:#ffffff;
  border-color:#111827;
}

/* preview actions */
.preview-actions { display:flex; justify-content:flex-end; gap:10px; margin-top:10px; padding-top:10px; border-top:1px solid #EEF2F6; }

/* alternates */
.current-alternates { margin-bottom:12px; }
.section-title { font-size:12px; font-weight:700; color:#374151; margin-bottom:14px; }
.alternates-list { display:flex; flex-direction:column; gap:10px; max-height: 260px; overflow-y: auto; padding-right: 6px; }
.alternate-row { display:flex; align-items:center; justify-content:space-between; gap:12px; padding:10px 12px; border-radius:8px; border:1px solid #EEF2F6; background:#ffffff; cursor:pointer; transition: transform 140ms ease, box-shadow 140ms ease; }
.alternate-row:hover { transform: translateY(-2px); box-shadow: 0 8px 20px rgba(2,6,23,0.06); }
.alternate-row__left { display:flex; flex-direction:column; gap:4px; min-width:0; }
.alternate-name { font-weight:600; color:#111827; font-size:15px; white-space:nowrap; overflow:hidden; text-overflow:ellipsis; }
.alternate-id { font-family:monospace; font-size:11px; color:#6B7280; }

/* status badge */
.alternate-status { display:inline-block; padding:6px 10px; font-size:12px; border-radius:999px; font-weight:700; min-width:72px; text-align:center; }
.alternate-status[data-status="COMPLETED"] {
  background: #ecfdf3;      /* light green */
  color: #166534;           /* dark green text */
  border: 1px solid #16a34a; /* dark green border */
}
.alternate-status[data-status="REJECTED"] { background:#FEF2F2; color:#7F1D1D; border:1px solid rgba(127,29,29,0.06); }
.alternate-status:not([data-status="COMPLETED"]):not([data-status="REJECTED"]) { background:#FFFBEB; color:#92400E; border:1px solid rgba(146,64,14,0.06); }

/* form grid */
.form-grid { display:grid; grid-template-columns: 1fr 320px; gap:20px; align-items:start; }
@media (max-width:920px) { .form-grid { grid-template-columns: 1fr; } }

/* form fields */
.form-main { display:flex; flex-direction:column; gap:12px; }
.form-label { font-size:11px; color:#6B7280; margin-bottom:2px; display:block; font-weight:600; }
.form-select, .form-input, .form-textarea { width:100%; padding:10px 12px; border:1px solid #E6E9EF; border-radius:8px; background:#ffffff; font-size:13px; color:#111827; box-sizing:border-box; transition: box-shadow .12s ease, border-color .12s ease; }
.form-select:focus, .form-input:focus, .form-textarea:focus { outline:none; border-color: rgba(17,24,39,0.12); box-shadow: 0 8px 20px rgba(2,6,23,0.06); }
.two-col { display:grid; grid-template-columns: 1fr 1fr; gap:12px; }
.field { display:flex; flex-direction:column; gap:2px; }

/* muted input */
.muted-input {
  background: #F3F4F6;
  color: #9CA3AF;
}

/* textarea */
.form-textarea { min-height: 110px; resize: vertical; }

/* add CTA */
.add-cta { margin-top: 12px; }
.add-cta__btn { width:100%; padding:10px 12px; border-radius:8px; border:2px dashed #D1D5DB; background:transparent; color:#6B7280; font-weight:500; font-size:13px; transition: border-color .12s ease, color .12s ease, background .12s ease; }
.add-cta__btn:hover, .add-cta__btn:focus { border-color:#111827; color:#0F172A; background: rgba(15,23,42,0.02); }

/* buttons */
.form-actions { display:flex; justify-content:flex-end; gap:12px; margin-top:6px; width:100%; box-sizing:border-box; }
.alt-btn { display:inline-flex; align-items:center; justify-content:center; gap:8px; padding:8px 12px; border-radius:4px; font-weight:500; border:1px solid transparent; cursor:pointer; font-size:13px; }
.btn-ghost {
  background: transparent;
  color: #4B5563;
  padding: 8px 12px;
  font-size: var(--text-base);
  border-radius: 4px;
  cursor: pointer;
}
.btn-ghost:hover { background: #F3F4F6; }
.alt-btn[disabled] { opacity:0.6; cursor:not-allowed; }
.alt-btn:not(.btn-ghost) { background:#111827; color:#fff; border:none; }

/* inventory dropdown */
.inventory-dropdown { position: relative; width: 100%; max-width: none; }
.inventory-dropdown__control { display:flex; align-items:center; justify-content:space-between; padding:10px 12px; border:1px solid #E6E9EF; border-radius:1px; background:#fff; cursor:pointer; font-size:13px; min-height:40px; }
.inventory-dropdown__control.open { box-shadow: 0 10px 30px rgba(2,6,23,0.06); border-color: rgba(17,24,39,0.08); }
.inventory-dropdown__control .selected { color:#111827; font-weight:500; display:flex; align-items:center; min-width:0; }
.inventory-dropdown__control .muted { color:#9CA3AF; font-weight:600; }
.selected-name {
  display: inline-block;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: calc(100% - 120px);
  vertical-align: middle;
}
.selected-id {
  display: inline-block;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 120px;
  margin-left: 6px;
  font-family: monospace;
  font-size: var(--text-s);
  color: #6B7280;
  vertical-align: middle;
}

/* menu */
.inventory-dropdown__menu { position:absolute; top: calc(100% + 8px); left:0; width:100%; background:#fff; border:1px solid #EEF2F6; border-radius:8px; box-shadow:0 12px 36px rgba(2,6,23,0.08); z-index:60; display:flex; flex-direction:column; max-height:320px; overflow:hidden; }
.inventory-dropdown__search { padding:10px; border-bottom:1px solid #F3F4F6; flex-shrink:0; }
.inventory-search { width:100%; padding:8px 10px; border-radius:6px; border:1px solid #EEF2F6; font-size:13px; }

/* options */
.inventory-dropdown__list { overflow-y:auto; max-height:220px; padding:8px; }
.inventory-option { padding:10px 12px; border-radius:6px; display:flex; align-items:center; justify-content:space-between; gap:8px; cursor:pointer; min-width:0; }
.inventory-option:hover { background:#FAFBFD; }

.opt-left { display:flex; align-items:center; gap:8px; min-width:0; }

.single-line { width:100%; display:flex; align-items:center; gap:8px; min-width:0; }
.opt-name-compact { font-weight:500; color:#111827; font-size:13px; display:inline-block; min-width:0; overflow:hidden; text-overflow:ellipsis; white-space:nowrap; }
.opt-id-compact { font-family: monospace; font-size:11px; color:#6B7280; opacity:0.95; flex:0 0 auto; max-width:110px; overflow:hidden; white-space:nowrap; text-overflow:ellipsis; }

/* inline loaders and controls */
.dropdown-loading,
.dropdown-loading-more { display:flex; align-items:center; justify-content:center; padding:12px; }
.dropdown-load-more { display:flex; justify-content:center; padding:10px; }

/* comparison preview */
.comparison-preview { background:#fff; border:1px solid #E5E7EB; border-radius:8px; padding:14px; display:flex; flex-direction:column; gap:12px; box-sizing:border-box; min-height:220px; box-shadow: 0 1px 2px 0 rgb(0 0 0 / 0.05); }
.preview-title { font-size:12px; font-weight:700; color:#374151; text-transform:uppercase; }

.preview-block { border-radius:8px; padding:12px; border:1px solid #EEF2F6; background:#FAFBFC; }
.preview-block.primary { background:#F3F4F6; border-color:#E5E7EB; }
.preview-block.alternate { background:#F7FBFF; border-color:#DBEAFE; }

.preview-label { font-size:10px; font-weight:700; color:#6B7280; text-transform:uppercase; letter-spacing:0.04em; }
.preview-name { font-size:13px; margin-top:6px; font-weight:500; color:#111827; }
.preview-id { font-family:monospace; font-size:11px; color:#6B7280; margin-top: 2px; }

/* circular arrow */
.preview-arrow {
  width: 25px;
  height: 25px;
  border-radius: 50%;
  background: #F3F4F6;
  border: 1px solid #E5E7EB;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 2px auto;
}
.preview-arrow__icon { font-size: var(--text-l); color: #6B7280; line-height:1; }
.preview-arrow img { width: 16px; height: 16px; object-fit: contain; }

.preview-reason { margin-top:8px; border-top:1px solid #DBEAFE; padding-top:8px; }
.preview-reason__label { font-size:10px; font-weight:700; color:#6B7280; }
.preview-reason__text { font-size:12px; color:#111827; font-style:italic; }

.no-alternates { margin-top: 8px; color: #6B7280; font-style: italic; }
.muted { color:#9CA3AF; font-weight:600; font-size:13px; }
.no-results { padding:12px; color:#6B7280; text-align:center; }

/* responsive adjustments */
@media (max-width:920px) {
  .form-grid { grid-template-columns: 1fr; }
  .comparison-preview { order:2; margin-top:14px; }
  .alternate-modal.size-md { max-width:95%; }
  .inventory-dropdown { max-width:100%; }
  .workflow-step:hover .step-card { transform: translateX(6px) translateY(-3px); }
  .step-card__pointer { left: -5px; top: 14px; width: 10px; height: 10px; }
  .workflow-list { --connector-extend: 36px; gap:14px; padding-left: 34px; }
}
@media (max-width:480px) {
  .am-header { padding:12px; }
  .am-body { padding:12px; }
  .step-card { padding:10px; }
  .inventory-dropdown__menu { max-height:50vh; }
  .workflow-list { max-height:300px; }
}
.req {
  color: #DC2626;
  margin-left: 2px;
  font-weight: 900;
}
</style>
